import { css } from "@emotion/react";
import MuiSwitch from "@mui/material/Switch";

import {
    CheckCircleIcon,
    colors,
    RemoveCircleIcon
} from "@dotnet/telematics-design-system";

import { svgFontSize } from "shared";

import { useStyles } from "./hooks";
import { CombinedSwitchProps, SWITCH_SIZES } from "./types";

const Switch = ({
    size,
    labelLeft,
    labelRight,
    customStyles,
    ...props
}: CombinedSwitchProps) => {
    const { blue400, blue500, blue700, blue800, textDisabled, white } = colors;

    const { options, getLabelStyles } = useStyles();

    const width = options[size].width;
    const height = options[size].height;
    const thumb = options[size].thumb;
    const translateX = options[size].translateX;
    const iconSize = options[size].iconSize;

    const isSmall = size === SWITCH_SIZES.Small;

    return (
        <div
            css={css({
                display: "flex",
                alignItems: "center",
                ...customStyles
            })}
        >
            {labelLeft && (
                <span
                    css={css({
                        marginRight: "12px",
                        ...getLabelStyles(props.disabled)
                    })}
                >
                    {labelLeft}
                </span>
            )}

            <MuiSwitch
                {...props}
                disableRipple
                {...(!isSmall && {
                    icon: <RemoveCircleIcon />,
                    checkedIcon: <CheckCircleIcon />
                })}
                sx={{
                    "&.MuiSwitch-root": {
                        width,
                        height,
                        padding: 0,

                        "&:hover": {
                            "& .MuiSwitch-track": {
                                background: blue500
                            },

                            "& .Mui-checked": {
                                "& + .MuiSwitch-track": {
                                    background: blue800
                                },

                                "&.Mui-disabled": {
                                    "& + .MuiSwitch-track": {
                                        background: textDisabled
                                    }
                                }
                            }
                        }
                    },

                    "& .MuiSwitch-switchBase": {
                        padding: 0,
                        top: 0,
                        bottom: 0,
                        left: isSmall ? 1 : 0,
                        borderRadius: 0,

                        "& > svg": {
                            ...(iconSize !== null && svgFontSize(iconSize))
                        },

                        "&.Mui-checked": {
                            color: white,
                            transform: `translateX(${translateX}px)`,

                            "& + .MuiSwitch-track": {
                                background: blue700,
                                opacity: 1
                            },

                            "&.Mui-disabled": {
                                color: white
                            }
                        },

                        "&.Mui-disabled": {
                            color: white,

                            "& + .MuiSwitch-track": {
                                background: textDisabled,
                                opacity: 1
                            }
                        },

                        "&:hover": {
                            background: "transparent"
                        }
                    },

                    "& .MuiSwitch-thumb": {
                        width: thumb,
                        height: thumb,
                        borderRadius: "11px",
                        boxShadow: "none"
                    },

                    "& .MuiSwitch-track": {
                        background: blue400,
                        borderRadius: "12px",
                        opacity: 1
                    }
                }}
            />

            {labelRight && (
                <span
                    css={css({
                        marginLeft: "12px",
                        ...getLabelStyles(props.disabled)
                    })}
                >
                    {labelRight}
                </span>
            )}
        </div>
    );
};

export { SWITCH_SIZES } from "./types";

export default Switch;
